import { observer } from 'mobx-react';
import React from 'react';
import { useWrapRequest } from 'use-wrap-request';
import { useSearchReducer } from '../../components/hooks/useSearchReducer';
import { Spinner } from '../../components/spinner';
import { TableComponent } from '../../components/table';
import { Api } from '../../core/api';
import {
    CustomFacilitySearchDto,
    FacilityDto
} from '../../core/api/StudioClient';
import { injectTSDI } from '../../core/tsdi';

interface NearbyFacilitiesProps {
    studio: CustomFacilitySearchDto;
}

export const NearbyFacilities = observer(
    ({ studio }: NearbyFacilitiesProps) => {
        const [searchParams, dispatch] = React.useReducer(useSearchReducer, {
            size: 30,
            page: 0
        });

        const { studioClient } = injectTSDI(Api);

        const facilities = useWrapRequest(
            async (searchParams, address, studioId) => {
                const result =
                    await studioClient.v1.publicFacilityControllerGetFacilities(
                        {
                            ...searchParams,
                            ...(address.latitude
                                ? { latitude: [address.latitude] }
                                : {}),
                            ...(address.longitude
                                ? { longitude: [address.longitude] }
                                : {}),
                            radius: 500,
                            geoRequestType: 'CIRCLE',
                            ...(studioId ? { excludeId: studioId } : {})
                        }
                    );

                return {
                    ...result.data,
                    content: result.data.content || []
                };
            },
            {
                defaultData: { content: [] },
                deps: [searchParams, studio.address, studio.id]
            }
        );

        return facilities.match({
            loading: () => (
                <div className="mt-8 flex justify-center">
                    <Spinner />
                </div>
            ),
            fetched: (data) =>
                data.totalElements && data.totalElements > 0 ? (
                    <div className="mt-8 mb-24 flex flex-col gap-3">
                        <h2 className="text-lg font-semibold tracking-tight">
                            Nearby studios
                        </h2>
                        <TableComponent<FacilityDto[]>
                            emptyText="No entries"
                            items={facilities}
                            columns={[
                                {
                                    Header: 'Name',
                                    accessor: 'name'
                                },
                                {
                                    Header: 'Category',
                                    accessor: (facility) =>
                                        facility.categoryWithTranslation.label
                                },
                                {
                                    Header: 'Status',
                                    accessor: 'status'
                                },
                                {
                                    Header: 'Facility type',
                                    accessor: 'facilityType'
                                }
                            ]}
                            onPageSizeChange={(size) =>
                                dispatch({ type: 'size', size })
                            }
                            onChangePage={(page) =>
                                void dispatch({
                                    type: 'page',
                                    page
                                })
                            }
                            renderRowSubComponent={RowDetails}
                        />
                    </div>
                ) : null
        });
    }
);

const RowDetails = ({ row }: { row: FacilityDto }) => {
    const {
        city,
        street,
        houseNumber,
        latitude,
        longitude,
        zip,
        countryWithTranslation
    } = row.address;

    return (
        <div className="flex flex-col gap-4 p-4">
            <h3 className="text-lg font-semibold tracking-tight">Address</h3>
            <div className="grid gap-4 md:grid-cols-2">
                <div className="flex flex-col gap-4">
                    <InfoBox label="City" text={city} />
                    <InfoBox label="Street" text={street} />
                    <InfoBox label="House Number" text={houseNumber} />
                </div>
                <div className="flex flex-col gap-4">
                    <InfoBox
                        label="Country"
                        text={countryWithTranslation?.translation}
                    />
                    <InfoBox label="Zip" text={zip} />
                    <InfoBox label="Latitude" text={latitude} />
                    <InfoBox label="Longitude" text={longitude} />
                </div>
            </div>
        </div>
    );
};

interface InfoBoxProps {
    label: string;
    text?: string | number;
}

const InfoBox = ({ label, text }: InfoBoxProps) => (
    <div className="flex flex-col">
        <span className="text-sm font-semibold">{label}</span>
        <span className="text-sm">{text}</span>
    </div>
);
